import React, { useState, useEffect, useRef } from 'react';
import { captureMessage } from "@sentry/react";
import API from '../../../API';
import NumberInput from './NumberInput';
import { T } from '../../../traks';
import { short_site_id, default_currency } from '../../../Site';
import { Row, Col, Button } from 'reactstrap';
import { DatePickerLayout } from './DatePicker';
import { calculateExtensionTotal, calculatePrice, determineExpirationDate, youSave } from './OrderUtils';
import { formatWithSeparators } from '../../../misc';
import OrderFooter from '../Globals/OrderFooter';
import { UpgradeGameSelectHandler } from './UpgradeGameSelect'
import { formatDate, formatPrice } from '../Globals/GlobalUtils';
import { PremiumPackageCard } from '../Package/PremiumPackageCard';
import { ExtraOptions } from '../Package/ExtraOptions';
import { calculatePackagePrice } from '../Package/PackageUtils';
import { determinePackageDescription, determinePackageName } from '../Cart/CartUtils';

export const OrderOverview = ({
  cart,
  setCart,
  game,
  setGame,
  setView,
  vatRate,
  preDefinedMonths,
  render_static,
  initialKeys,
  initialMonths,
  initialStartDate,
  initialPackageId,
  autoAddFromUrl,
}) => {
  const toPositiveIntOrNull = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  };
  const getTodayIsoDate = () => new Date().toISOString().split('T')[0];

  // Derived initial values
  // URL props are inputs; we apply them one time and then user changes win.
  const todayIso = getTodayIsoDate();
  const initialKeysInt = toPositiveIntOrNull(initialKeys);
  const initialMonthsInt = toPositiveIntOrNull(initialMonths);
  const initialPackageIdInt = toPositiveIntOrNull(initialPackageId);

  // Build your own package
  const [minimumOfKeys, setMinimumOfKeys] = useState(1);
  const [numberOfKeys, setNumberOfKeys] = useState(initialKeysInt !== null && initialKeysInt !== undefined ? initialKeysInt : 1);
  const [minimumOfMonths, setMinimumOfMonths] = useState(1);
  const [numberOfMonths, setNumberOfMonths] = useState(initialMonthsInt !== null && initialMonthsInt !== undefined
    ? initialMonthsInt
    : (preDefinedMonths !== null && preDefinedMonths !== undefined ? preDefinedMonths : 1));
  const [gameTitle, setGameTitle] = useState(null);
  const [prices, setPrices] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate !== null && initialStartDate !== undefined ? initialStartDate : todayIso);
  const [dateError, setDateError] = useState(null);

  // Pre-defined packages and extra options
  const [packages, setPackages] = useState({});
  const [packagesNoDiscount, setPackagesNoDiscount] = useState({});
  const [selectedExtraOption, setSelectedExtraOption] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDetails, setPackageDetails] = useState({});
  const [extraOptionsMonths, setExtraOptionsMonths] = useState([]);
  const [perKeyPrice, setPerKeyPrice] = useState(1.0);
  const [perKeyPriceNoDiscount, setPerKeyPriceNoDiscount] = useState(1.0);

  const doesPackageExist = (packageId, packages, packageDetails) => {
    if (!packageId) return false;
    const key = String(packageId);
    return (
      (packages && (packages[key] != null || packages[packageId] != null)) ||
      (packageDetails && (packageDetails[key] != null || packageDetails[packageId] != null))
    );
  };

  const hasAppliedInitial = useRef({ keys: false, months: false, startDate: false });
  const hasAppliedInitialPackage = useRef(false);
  const hasAutoAdded = useRef(false);

  // Apply initial build-your-own values once. Do not override user changes.
  useEffect(() => {
    if (initialPackageIdInt) return;

    if (initialKeysInt !== null && !hasAppliedInitial.current.keys) {
      setNumberOfKeys(initialKeysInt);
      hasAppliedInitial.current.keys = true;
    }
    if (initialMonthsInt !== null && !hasAppliedInitial.current.months) {
      setNumberOfMonths(initialMonthsInt);
      hasAppliedInitial.current.months = true;
    }
    if (initialStartDate && !hasAppliedInitial.current.startDate) {
      setSelectedStartDate(initialStartDate);
      hasAppliedInitial.current.startDate = true;
    }
  }, [initialKeysInt, initialMonthsInt, initialStartDate, initialPackageIdInt]);

  // Wait until packages data is loaded, then preselect package once.
  useEffect(() => {
    const packageId = initialPackageIdInt;
    if (!packageId) return;
    if (hasAppliedInitialPackage.current) return;
    if (!doesPackageExist(packageId, packages, packageDetails)) return;

    setSelectedPackage(packageId);
    hasAppliedInitialPackage.current = true;
  }, [initialPackageId, initialPackageIdInt, packages, packageDetails]);
  
  // Fetch the data necessary (prices, discounts, packages, extra options)
  useEffect(() => {
    API.get('/z/premium_prices', { site_id: short_site_id }, (response, status) => {
      if (status >= 200 && status < 300) {
        setPrices(response.prices);
        setDiscounts(response.discounts);
        setPackages(response.packages);
        setPackagesNoDiscount(response.packages_no_discount);
        setPackageDetails(response.package_details);
        setExtraOptionsMonths(response.extra_options_months);
        setPerKeyPrice(response.per_key_price);
        setPerKeyPriceNoDiscount(response.per_key_price_no_discount);
        setCart((prev) => ({
          ...prev,
          prices: response.base_price,
          discounts: response.discounts,
          packages: response.packages,
          selectedPackage: selectedPackage,
          package_details: response.package_details,
          extra_options_months: response.extra_options_months,
          packages_no_discount: response.packages_no_discount,
          per_key_price: response.per_key_price,
          per_key_price_no_discount: response.per_key_price_no_discount
        }));
      }

        if (status < 200 || status >= 300) {
            captureMessage("Failed to fetch premium prices", {
                level: "error",
                extra: {
                    response: response,
                    status: status
                }
            });
        }
    });
  }, []);

useEffect(() => {
  if (cart.lines && cart.lines.length > 0) {
    const line = cart.lines[0];
    const pkgId = line.packageId;
    const extraOptionId = line.extraOptionId;

    if (line.addedVia === 'predefined_package' && pkgId) {
      setSelectedPackage(pkgId);
      setSelectedExtraOption(extraOptionId || null);
    } else if (line.addedVia === 'build_your_own') {
      setSelectedPackage(null);
      setSelectedExtraOption(null);
      setNumberOfKeys(line.keys);
      setNumberOfMonths(line.months);
      if (line.startDate) setSelectedStartDate(line.startDate);
      if (line.game) setGameTitle(line.game);
    }
  } else {
    const shouldPreserveInitialPackage =
      initialPackageIdInt &&
      (!hasAppliedInitialPackage.current || selectedPackage === initialPackageIdInt);
    if (shouldPreserveInitialPackage) return;
    setSelectedPackage(null);
    setSelectedExtraOption(null);
  }
}, [cart, initialPackageIdInt]);

  const handleAddToCart = () => {
    if (!selectedPackage && (!selectedStartDate || selectedStartDate.trim() === "")) {
      setDateError("* Please select a start date");
      return;
    }

    if (selectedPackage) {
      setSelectedStartDate(new Date().toISOString().split('T')[0]);
    } else {
      setDateError(null);
    }

    const expirationDate = determineExpirationDate(selectedStartDate, numberOfMonths);

    const newLine = {
      keys: numberOfKeys,
      months: numberOfMonths,
      discountedTotal: total,
      discountedTotalNoVat: totalNoVat,
      startDate: selectedStartDate,
      expirationDate: new Date(expirationDate).toISOString().split('T')[0],
      package: null,
      extraOption: null,
      game: gameTitle,
      addedVia: 'build_your_own'
    };

    setSelectedPackage(null);
    setSelectedExtraOption(null);

    setCart((prev) => ({ ...prev, lines: [newLine] }));
    setView('cart');
  };

  const handleAddPackageToCart = () => {
    if (!selectedPackage) return;
    if (!packageDetails || !packageDetails[selectedPackage]) {
      captureMessage("Missing package details for selected package", {
        level: "warning",
        extra: {
          selectedPackage
        }
      });
      return;
    }

    const packageMonths = packageDetails[selectedPackage].numMonths || 1;
    const extraMonths = selectedExtraOption ? extraOptionsMonths[selectedExtraOption] || 0 : 0;
    const totalMonths = packageMonths + extraMonths;
    const expirationDate = determineExpirationDate(selectedStartDate, totalMonths);

    const lineItem = {
      packageId: selectedPackage,
      extraOptionId: selectedExtraOption,
      discountedTotal: finalTotal,
      discountedTotalNoVat: finalTotalNoVat,
      keys: selectedPackage ? packageDetails[selectedPackage].numKeys : 0,
      months: totalMonths,
      startDate: selectedStartDate,
      expirationDate: new Date(expirationDate).toISOString().split('T')[0],
      game: null,
      addedVia: 'predefined_package',
    };

    setNumberOfKeys(1);
    setNumberOfMonths(preDefinedMonths || 1);
    setSelectedStartDate(new Date().toISOString().split('T')[0]);
    setGameTitle(null);

    setCart((prev) => ({ ...prev, lines: [lineItem] }));
    setView('cart');
  };

  // Build your own package
  const { total, totalNoVat, discountedTotal, discount } = calculatePrice(
    prices,
    discounts,
    numberOfKeys,
    numberOfMonths,
    vatRate
  );

  // Pre-defined packages
    const packageData = {
    selectedPackage,
    selectedExtraOption,
    packages,
    packagesNoDiscount,
    vatRate: vatRate
  }
  const { packageTotal, packageTotalNoVat } = calculatePackagePrice(packageData);

  const hasPackageInCart = (cart.lines || []).some(
    (line) => line.addedVia === 'predefined_package' || line.addedVia === 'build_your_own'
  );

  const extra5Months = extraOptionsMonths["1"] || 5;
  const extra11Months = extraOptionsMonths["2"] || 11;

  const extensionTotal5 = calculateExtensionTotal(extra5Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: true
  });

  const extensionTotal11 = calculateExtensionTotal(extra11Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: true
  });

  // No discount
  const extensionNoDiscountTotal5 = calculateExtensionTotal(extra5Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: false
  });

  const extensionNoDiscountTotal11 = calculateExtensionTotal(extra11Months, {
    selectedPackage,
    packageDetails,
    numberOfKeys,
    perKeyPrice,
    perKeyPriceNoDiscount,
    useDiscount: false
  });

  let finalTotal, finalTotalNoVat;
  const isPackageSelected = !!selectedPackage;

  if (isPackageSelected) {
    if (selectedExtraOption === "1") {
      finalTotal = packageTotal + extensionTotal5;
      finalTotalNoVat = packageTotalNoVat + (extensionTotal5 / (1 + vatRate / 100));
    } else if (selectedExtraOption === "2") {
      finalTotal = packageTotal + extensionTotal11;
      finalTotalNoVat = packageTotalNoVat + (extensionTotal11 / (1 + vatRate / 100));
    } else {
      finalTotal = packageTotal;
      finalTotalNoVat = packageTotalNoVat;
    }
  } else {
    finalTotal = total;
    finalTotalNoVat = totalNoVat;
  }

  //  Total saved amount calculation
  const baselineNoDiscount = selectedPackage ? packagesNoDiscount[selectedPackage]: 0;
  const totalPackageSaved = youSave(baselineNoDiscount, finalTotal, selectedExtraOption, extensionNoDiscountTotal5, extensionNoDiscountTotal11);

  useEffect(() => {
    if (!autoAddFromUrl || hasAutoAdded.current) return;

    if (initialPackageIdInt) {
      if (!selectedPackage) return;
      if (!doesPackageExist(selectedPackage, packages, packageDetails)) return;
      if (!packageDetails || !packageDetails[String(selectedPackage)]) return;
      if (!finalTotal || !finalTotalNoVat) return;
      handleAddPackageToCart();
      hasAutoAdded.current = true;
      return;
    }

    const hasBuildInputs = initialKeysInt || initialMonthsInt || initialStartDate;
    if (!hasBuildInputs) return;
    if (!prices || Object.keys(prices).length === 0) return;
    if (!discounts || Object.keys(discounts).length === 0) return;
    if (!selectedStartDate || !numberOfKeys || !numberOfMonths) return;
    if (!total || !totalNoVat) return;

    handleAddToCart();
    hasAutoAdded.current = true;
  }, [
    autoAddFromUrl,
    initialPackageIdInt,
    initialKeysInt,
    initialMonthsInt,
    initialStartDate,
    selectedPackage,
    packages,
    packageDetails,
    finalTotal,
    finalTotalNoVat,
    prices,
    discounts,
    selectedStartDate,
    numberOfKeys,
    numberOfMonths,
    total,
    totalNoVat
  ]);

  return (
    <div id="cart-order" className="p-4">
      <div className="cart-order-inner pb-5">
        <h4 className='mb-4'><T>Option 1: Choose your package</T></h4>

        <Row className='mb-4'>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
              id={1}
              className="h-100 w-100 mb-3"
              title={determinePackageName(1)}
              description={determinePackageDescription(1)}
              discountedPrice={packages["1"]}
              price={packagesNoDiscount["1"]}
              binding={<T>No commitment</T>}
              setSelectedPackage={setSelectedPackage}
              selectedPackage={selectedPackage}
              details={[
                <T>30 Invitations</T>,
                <T>30 days premium access</T>,
                <T>All features and markets</T>,
              ]}
            />
          </Col>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
              id={2}
              className="h-100 w-100"
              title={determinePackageName(2)}
              description={determinePackageDescription(2)}
              discountedPrice={packages["2"]}
              price={packagesNoDiscount["2"]}
              setSelectedPackage={setSelectedPackage}
              selectedPackage={selectedPackage}
              binding={<T>No commitment</T>}
              details={[
                <T>35 Invitations</T>,
                <T>30 days premium access</T>,
                <T>All features and markets</T>,
              ]}
            />
          </Col>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
              <PremiumPackageCard 
                id={5}
                className="h-100 w-100"
                title={determinePackageName(5)}
                description={determinePackageDescription(5)}
                discountedPrice={packages["5"]}
                price={packagesNoDiscount["5"]}
                setSelectedPackage={setSelectedPackage}
                selectedPackage={selectedPackage}
                binding={<T>No commitment</T>}
                details={[
                    <T>50 Invitations</T>,
                    <T>30 days premium access</T>,
                    <T>All features and markets</T>,
                ]}
            />
          </Col>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
              id={3}
              className="h-100 w-100"
              title={determinePackageName(3)}
              discountedPrice={packages["3"]}
              price={packagesNoDiscount["3"]}
              setSelectedPackage={setSelectedPackage}
              selectedPackage={selectedPackage}
              binding={<T>No commitment</T>}
              details={[
                <T>100 Invitations</T>,
                <T>30 days premium access</T>,
                <T>All features and markets</T>,
              ]}
            />
          </Col>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
              id={4}
              className="h-100 w-100"
              title={determinePackageName(4)}
              discountedPrice={packages["4"]}
              price={packagesNoDiscount["4"]}
              setSelectedPackage={setSelectedPackage}
              selectedPackage={selectedPackage}
              binding={<T>No commitment</T>}
              details={[
                <T>200 Invitations</T>,
                <T>30 days premium access</T>,
                <T>All features and markets</T>,
              ]}
            />
          </Col>
          <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
                id={6}
                className="h-100 w-100"
                title={determinePackageName(6)}
                description={determinePackageDescription(6)}
                discountedPrice={packages["6"]}
                price={packagesNoDiscount["6"]}
                setSelectedPackage={setSelectedPackage}
                selectedPackage={selectedPackage}
                binding={<T>No commitment</T>}
                details={[
                    <T>500 Invitations</T>,
                    <T>30 days premium access</T>,
                    <T>All features and markets</T>,
                ]}
            />
        </Col>
        <Col lg="3" md="12" sm="12" className="d-flex pb-4">
            <PremiumPackageCard 
                id={7}
                className="h-100 w-100"
                title={determinePackageName(7)}
                description={determinePackageDescription(7)}
                discountedPrice={packages["7"]}
                price={packagesNoDiscount["7"]}
                setSelectedPackage={setSelectedPackage}
                selectedPackage={selectedPackage}
                binding={<T>No commitment</T>}
                details={[
                    <T>10 Invitations</T>,
                    <T>30 days premium access</T>,
                    <T>All features and markets</T>,
                ]}
            />
        </Col>
        </Row>

        {selectedPackage && (
          <React.Fragment>
            <Row className='mb-4'>
              <ExtraOptions 
                titleClasses="mb-3"
                selectedExtraOption={selectedExtraOption} 
                setSelectedExtraOption={setSelectedExtraOption} 
                extensionTotal5={extensionTotal5}
                extensionTotal11={extensionTotal11}
              />
            </Row>
            <Row className="align-items-center mt-2 mb-3">
              <Col>
                <h5>
                  <T>Package price:</T> {formatPrice(finalTotal)} {default_currency}
                </h5>
              </Col>
              <Col className="d-flex justify-content-end align-items-center">
                <Button
                  className="ad_new_game_button px-4 py-2 btn btn-secondary"
                  onClick={handleAddPackageToCart}
                >
                  {hasPackageInCart ? <T>Update cart</T> : <T>Add to cart</T>}
                </Button>
              </Col>
            </Row>
          </React.Fragment>
        )}

        <hr className='bg-black'/>

        <h4 className='mb-4'>
          <T>Option 2: Build your own package</T>
        </h4>
        <NumberInput
          label={<T>Number of participants</T>}
          imgSrc='https://static.aktiedysten.dk/gfx/user-icon-green-2023-0.svg'
          value={numberOfKeys}
          min={minimumOfKeys}
          onIncrement={() => setNumberOfKeys(prev => prev + 1)}
          onDecrement={() => setNumberOfKeys(prev => Math.max(minimumOfKeys, prev - 1))}
          onChange={e => setNumberOfKeys(Number(e.target.value))}
          containerClassName='mb-4'
        />

        <NumberInput
          label={<T>Number of months</T>}
          imgSrc='/gfx/calendar-color-0.svg'
          value={numberOfMonths}
          onIncrement={() => setNumberOfMonths((prev) => prev + 1)}
          onDecrement={() => setNumberOfMonths((prev) => {
            if (prev - 1 >= minimumOfMonths) {
                return Math.max(1, prev - 1)
            } else {
                return prev
            }
            })
          }
          onChange={(e) => setNumberOfMonths(Number(e.target.value))}
          min={minimumOfMonths}
          containerClassName='mb-4'
        />

        <DatePickerLayout 
            label={<T>Start date</T>} 
            imgSrc='/gfx/calendar-color-0.svg'
            selectedStartDate={selectedStartDate} 
            setSelectedStartDate={setSelectedStartDate}
            containerClassName="mb-4" 
        />

         <UpgradeGameSelectHandler
          label={<T>Upgrade an ongoing game</T>} 
          imgSrc='https://static.aktiedysten.dk/gfx/games-icon-2023-0.svg'  
          game={game}
          setGame={setGame}
          setGameTitle={setGameTitle}
          setNumberOfKeys={setNumberOfKeys}
          setMinimumOfKeys={setMinimumOfKeys}
          setNumberOfMonths={setNumberOfMonths}
          setMinimumOfMonths={setMinimumOfMonths}
          containerClassName='mb-4'
        />

        {dateError && (
            <Row className="mb-3">
                <Col>
                <div style={{ color: 'red' }}>{dateError}</div>
                </Col>
            </Row>
        )}

        <Row className="align-items-center mt-2 mb-3">
          <Col>
            <h5>
              <T>Total Price:</T> {formatWithSeparators(total.toFixed(2))} {default_currency}
            </h5>
            {discount > 0 && (
              <span className="text-success">
                <T>Discount:</T> {discount}%, <T>you save</T> {formatWithSeparators(discountedTotal)} {default_currency}
              </span>
            )}
            <div>
              <T>Premium valid until</T>: {formatDate(determineExpirationDate(selectedStartDate, numberOfMonths))}
            </div>
          </Col>
          <Col className="d-flex justify-content-end align-items-center">
            <Button className="ad_new_game_button px-4 py-2 btn btn-secondary" onClick={handleAddToCart}>
              {cart.lines && cart.lines.length !== 0 ? <T>Update Cart</T> : <T>Add to Cart</T>}
            </Button>
          </Col>
        </Row>

      </div>
      <OrderFooter
        total={finalTotal}
        totalNoVat={finalTotalNoVat}
        onNext={() => setView('cart')}
        nextLabel={<T>Go to cart</T>}
        hideNextButton={!cart || !cart.lines || cart.lines.length === 0}
        backLabel={null}
      />
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/c/PremiumKeys/Order/OrderOverview.js